import { Input } from '@paulownia/shared/components/ui/input';
import { Label } from '@paulownia/shared/components/ui/label';
import { Textarea } from '@paulownia/shared/components/ui/textarea';
import { Store, Info } from 'lucide-react';
import { SlugInput } from '../components/SlugInput';
import type { TenantInfoFormData } from '../hooks/useOnboardingState';
import type { SlugStatus } from '../hooks/useSlugValidation';

interface TenantInfoStepProps {
  data: TenantInfoFormData;
  slugStatus: SlugStatus;
  onUpdate: (updates: Partial<TenantInfoFormData>) => void;
  onSlugChange: (slug: string) => void;
}

/**
 * Step 1: Business information form
 *
 * Required fields:
 * - Business name (2-100 characters)
 * - Slug/URL (unique, 3+ chars, [a-z0-9-])
 *
 * Optional fields:
 * - Description (max 500 chars)
 * - Phone number
 * - Opening hours
 */
export function TenantInfoStep({ data, slugStatus, onUpdate, onSlugChange }: TenantInfoStepProps) {
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate({ name: e.target.value });
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onUpdate({ description: e.target.value });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate({ phoneNumber: e.target.value });
  };

  const handleOpeningHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate({ openingHours: e.target.value });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
          <Store className="w-5 h-5 text-orange-600" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Votre commerce</h2>
          <p className="text-sm text-gray-500">Configurez les informations de base</p>
        </div>
      </div>

      {/* Required fields */}
      <div className="space-y-4">
        {/* Business name */}
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-medium">
            Nom du commerce <span className="text-red-500">*</span>
          </Label>
          <Input
            id="name"
            type="text"
            value={data.name}
            onChange={handleNameChange}
            placeholder="La Rôtisserie du Marché"
            maxLength={100}
            className="w-full"
          />
          <p className="text-xs text-gray-500">{data.name.length}/100 caractères</p>
        </div>

        {/* Slug */}
        <SlugInput
          value={data.slug}
          status={slugStatus}
          onChange={onSlugChange}
        />
      </div>

      {/* Optional fields separator */}
      <div className="border-t pt-6 mt-6">
        <div className="bg-gray-50 p-3 rounded-lg flex gap-2 text-sm mb-4">
          <Info className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
          <p className="text-gray-600">
            Les informations suivantes sont optionnelles. Vous pourrez les compléter plus tard
            depuis les Paramètres.
          </p>
        </div>

        <div className="space-y-4">
          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">
              Description
            </Label>
            <Textarea
              id="description"
              value={data.description || ''}
              onChange={handleDescriptionChange}
              placeholder="Poulets rôtis fermiers et accompagnements maison depuis 1985."
              maxLength={500}
              rows={3}
              className="w-full resize-none"
            />
            <p className="text-xs text-gray-500">{(data.description || '').length}/500 caractères</p>
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-sm font-medium">
              Téléphone
            </Label>
            <Input
              id="phone"
              type="tel"
              value={data.phoneNumber || ''}
              onChange={handlePhoneChange}
              placeholder="06 12 34 56 78"
              className="w-full"
            />
          </div>

          {/* Opening hours */}
          <div className="space-y-2">
            <Label htmlFor="hours" className="text-sm font-medium">
              Heures d'ouverture
            </Label>
            <Input
              id="hours"
              type="text"
              value={data.openingHours || ''}
              onChange={handleOpeningHoursChange}
              placeholder="Mar-Sam: 9h-19h, Dim: 9h-13h"
              className="w-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
